import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = new Set();
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;

    dialog.addEventListener('scroll', this.handleScroll);
    dialog.addEventListener('keydown', this.handleKeyNavigation);
    dialog.setAttribute('tabindex', '0');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;

    dialog.removeEventListener('scroll', this.handleScroll);
    dialog.removeEventListener('keydown', this.handleKeyNavigation);
  }

  /* ---------------- OPEN ---------------- */

  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#activeIndex = index;

    const open = () => {
      dialog.showModal();

      // 🔒 Disable page scroll
      document.body.style.overflow = 'hidden';

      dialog.focus();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li,slideshow-slide')
        : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const transitionName = 'gallery-item';
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /* ---------------- KEYBOARD NAV ---------------- */

  handleKeyNavigation = (event) => {
    const { media } = this.refs;
    if (!media?.length) return;

    let nextIndex = this.#activeIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = Math.min(media.length - 1, this.#activeIndex + 1);
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = Math.max(0, this.#activeIndex - 1);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (nextIndex !== this.#activeIndex) {
      event.preventDefault();
      this.selectThumbnail(nextIndex, { behavior: 'smooth' });
    }
  };

  /* ---------------- HIGH RES IMAGE ---------------- */

  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image) return;

    const highResUrl = image.getAttribute('data_max_resolution');
    if (!highResUrl || this.#highResImagesLoaded.has(highResUrl)) return;

    preloadImage(highResUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResUrl);

    newImage.onload = () => {
      newImage.style.maxWidth = '90vw';
      newImage.style.maxHeight = '90vh';
      newImage.style.objectFit = 'contain';

      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResUrl);
    };

    newImage.src = highResUrl;
  }

  /* ---------------- SCROLL ---------------- */

  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisible = await getMostVisibleElement(media);
    const index = media.indexOf(mostVisible);
    if (index === -1) return;

    this.#activeIndex = index;

    Array.from(thumbnails.querySelectorAll('button')).forEach((btn, i) =>
      btn.setAttribute('aria-selected', `${i === index}`)
    );

    this.#loadHighResolutionImage(mostVisible);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }, 50);

  /* ---------------- CLOSE ---------------- */

  async close() {
    const { dialog, media } = this.refs;

    // 🔓 Restore page scroll
    document.body.style.overflow = '';

    if (!supportsViewTransitions() || isLowPowerDevice()) {
      this.closeDialog();
      return;
    }

    const mostVisible = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisible);
    if (activeIndex === -1) {
      this.closeDialog();
      return;
    }

    const transitionName = 'gallery-item';
    const mediaGallery = this.closest('media-gallery');
    const slide =
      mediaGallery?.presentation === 'carousel'
        ? mediaGallery.slideshow?.slides?.[activeIndex]
        : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      this.closeDialog();
      return;
    }

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.thumbnails);

    mostVisible.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisible.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /* ---------------- THUMBNAILS ---------------- */

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;
    if (!media[index] || !thumbnails.children[index]) return;

    this.#activeIndex = index;

    Array.from(thumbnails.querySelectorAll('button')).forEach((btn, i) =>
      btn.setAttribute('aria-selected', `${i === index}`)
    );

    scrollIntoView(thumbnails.children[index], {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    media[index].scrollIntoView({ behavior: options.behavior });
    this.#loadHighResolutionImage(media[index]);

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((a, b) =>
          b.intersectionRatio > a.intersectionRatio ? b : a
        );
        observer.disconnect();
        resolve(mostVisible.target);
      },
      { threshold: Array.from({ length: 100 }, (_, i) => i / 100) }
    );

    elements.forEach(el => observer.observe(el));
  });
}
